// useClickOutside — dismiss a popover/menu when a pointer press lands outside
// every supplied element ref, or when Escape is pressed, while `active` is
// true. Listeners attach only while active, so an idle popover costs nothing.
// The latest callback + ref list are read through refs so the effect depends
// only on `active` (no re-subscribe churn, and the caller need not memoise the
// ref array). Mirrors the inline pattern in contract-overview/StatusPillButton.

import { useEffect, useRef, type RefObject } from "react";

export function useClickOutside(
  active: boolean,
  refs: Array<RefObject<HTMLElement | null>>,
  onOutside: () => void,
): void {
  const onOutsideRef = useRef(onOutside);
  const refsRef = useRef(refs);

  // Keep the refs current after each render so event handlers always read
  // the latest values without needing to re-subscribe the listeners.
  useEffect(() => {
    onOutsideRef.current = onOutside;
    refsRef.current = refs;
  });

  useEffect(() => {
    if (!active) return;
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (refsRef.current.some((r) => r.current?.contains(target))) return;
      onOutsideRef.current();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOutsideRef.current();
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [active]);
}
